import { FC } from 'react';
import { Container, Nav } from 'react-bootstrap';
import parse from 'html-react-parser';
import { useFeatureToggles } from '../../../contexts/FeatureTogglesContext';
import { useTranslation } from 'react-i18next';
import { getDetailCardIndexes, useHomeTranslationsQuery } from '../../../services/HomePageServices';
import { initialGoDown, transition, whileInView } from '../../../helpers/animations';
import { motion } from 'framer-motion';
import { conversion, triggerEvent } from '../../../helpers/cxDataLayer';
import { sanitizeCmsHtml } from '../../../helpers/sanitizeCmsHtml';

function ApplicationRequirementsSection() {
  const { application } = useFeatureToggles();
  const { t } = useTranslation(['detail', 'general_site']);
  const homeQuery = useHomeTranslationsQuery();
  const detailData = homeQuery.data ? getDetailCardIndexes(homeQuery.data) : undefined;
  const isLoading = homeQuery.isLoading;
  const isError = homeQuery.isError;

  const applicationRemarks = t('application_remarks');
  const applicationLink = t('application_link');
  const applicationButtonText = t('application_button_text');
  const hasApplicationRemarks = !!applicationRemarks && applicationRemarks !== 'application_remarks';
  const hasApplicationLink = !!applicationLink && applicationLink !== 'application_link';
  const hasApplicationButtonText = !!applicationButtonText && applicationButtonText !== 'application_button_text';
  const showApplicationContent = application && (hasApplicationRemarks || (hasApplicationLink && hasApplicationButtonText));

  if (isError) {
    return (
      <section id="application-requirements" className='application-requirements'>
        <Container className='application-requirements__container'>
          <p className="application-requirements__error" role="status">
            {t('loading_error_text', { ns: 'general_site',
              defaultValue: 'Content is temporarily unavailable. Please refresh and try again.' })}
          </p>
        </Container>
      </section>
    );
  }

  if (!detailData || isLoading) return null;

  return (
    <section id="application-requirements" className='application-requirements'>
      <Container className='application-requirements__container'>
        <div className="application-requirements__top">
          <div className='application-requirements__detail-group'>
            <motion.p
              initial={initialGoDown}
              whileInView={whileInView}
              viewport={{
                once: true,
                amount: 0.2
              }}
              transition={transition}
              className='application-requirements__name'
            >
              {t('application_requirements_section_name')}
            </motion.p>
            <motion.h2
              initial={initialGoDown}
              whileInView={whileInView}
              viewport={{
                once: true,
                amount: 0.2
              }}
              transition={transition}
              className='application-requirements__title'
            >
              {t('application_requirements_title')}
            </motion.h2>
          </div>
        </div>
        <div className="application-requirements__cards" role="list" aria-label={t('application_requirements_title')}>
          {detailData?.map((cardIndex) => (
            <div key={`application-requirements-card-${cardIndex}`} className="application-requirements__card-item" role="listitem">
              <ApplicationRequirementsCard
                cardIndex={cardIndex}
              />
            </div>
          ))}
        </div>
        <motion.div
          initial={initialGoDown}
          whileInView={whileInView}
          viewport={{
            once: true,
            amount: 0.2
          }}
          transition={transition}
          className='application-requirements__apply-button-wrapper'
        >
          {showApplicationContent ? (
            <>
              {hasApplicationRemarks ? (
                <div className='application-requirements__remarks'>
                  {parse(sanitizeCmsHtml(applicationRemarks))}
                </div>
              ) : null}
              {(hasApplicationLink && hasApplicationButtonText) ? (
                <Nav.Link
                  className="application-requirements__apply-button"
                  href={applicationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    conversion();
                    triggerEvent({
                      event:
                      {
                        event_action: "CATHAY_HACKATHON::APPLY_NOW_BTN"
                      }
                    });
                  }}
                >
                  {applicationButtonText}
                </Nav.Link>
              ) : null}
            </>
          ) : null}
        </motion.div>
      </Container>
    </section>
  );
}

export default ApplicationRequirementsSection;

interface ApplicationRequirementsCardProps {
  cardIndex: number;
}

const ApplicationRequirementsCard: FC<ApplicationRequirementsCardProps> = ({ cardIndex }) => {
  const { t } = useTranslation('detail');
  const iconAltText = t(`application_requirements_card_icon_alt_text_${cardIndex}`);
  const fallbackTitle = t(`application_requirements_card_title_${cardIndex}`);

  return (
    <motion.article
      initial={initialGoDown}
      whileInView={whileInView}
      viewport={{
        once: true,
        amount: 0.2
      }}
      transition={transition}
      className="application-requirements-card"
    >
      <div className='application-requirements-card__icon-col'>
        <img
          className='application-requirements-card__icon'
          src={t(`application_requirements_card_icon_link_${cardIndex}`)}
          alt={iconAltText && iconAltText !== `application_requirements_card_icon_alt_text_${cardIndex}` ? iconAltText : fallbackTitle}
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className='application-requirements-card__content-col'>
        <h3 className='application-requirements-card__title'>
          {fallbackTitle}
        </h3>
        <div className='application-requirements-card__description'>
          {t(`application_requirements_card_description_${cardIndex}`)}
        </div>
      </div>
    </motion.article>
  );
};